// Fichier: src/pages/StudentEvolutionDetails.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import moment from 'moment';
import 'moment/locale/fr';
import EvaluationList from './EvaluationList';

const TRACK_LABELS = {
  leisure:     'Loisir',
  foundation:  'Foundation',
  performance: 'Performance',
  competition: 'Compétition',
  freestyle:   'Freestyle',
  intensive:   'Intensif',
};

const SKILL_LABELS = {
  technique:       'Technique',
  musicality:      'Musicalité',
  freestyle:       'Freestyle',
  memorization:    'Mémorisation chorégraphique',
  stagePresence:   'Présence scénique',
  confidence:      'Confiance',
  energyIntention: 'Énergie / intention',
  learningSpeed:   "Rapidité d'apprentissage",
  precision:       'Précision',
  endurance:       'Endurance',
  versatility:     'Polyvalence',
  discipline:      'Discipline / régularité',
  personalWork:    'Travail personnel',
};

const statusLabel = (status) => {
  switch (status) {
    case 'completed':   return 'Terminé';
    case 'in_progress': return 'En cours';
    case 'paused':      return 'En pause';
    default:            return 'Pas commencé';
  }
};

const StudentEvolutionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProfile(); }, [id]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'student_evolution_profiles', id));
      if (!snap.exists()) { navigate('/student-evolutions'); return; }
      setProfile({ id: snap.id, ...snap.data() });
    } catch (error) {
      console.error('Erreur lecture suivi individuel:', error);
    } finally {
      setLoading(false);
    }
  };

  const averageProgress = useMemo(() => {
    const goals = profile?.seasonGoals || [];
    if (!goals.length) return 0;
    return Math.round(goals.reduce((sum, g) => sum + (Number(g.progress) || 0), 0) / goals.length);
  }, [profile]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  if (!profile) return null;

  const vision      = profile.vision || {};
  const skills      = profile.skills || {};
  const seasonGoals = profile.seasonGoals || [];
  const actionPlan  = profile.actionPlan || [];

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student-evolutions')} sx={{ mb: 2 }}>
        Retour à la liste
      </Button>

      {/* ── En-tête ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{profile.studentName}</Typography>
          <Typography color="text.secondary">Saison {profile.season || '2026-2027'}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip label={TRACK_LABELS[profile.track] || 'Track non défini'} color="primary" variant="outlined" />
            <Chip label={`${averageProgress}% de progression moyenne`} color="success" variant="outlined" />
          </Box>
        </Box>
        <Button
          variant="contained"
          component={Link}
          to={`/student-evolutions/edit/${id}`}
          sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
        >
          Modifier le profil
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* ── Colonne gauche ──────────────────────────────────────────────── */}
        <Grid item xs={12} md={7}>

          {/* Vision */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Vision de la danse</Typography>
              <Typography variant="subtitle2">Pourquoi elle danse</Typography>
              <Typography sx={{ mb: 2 }}>{vision.whyDance || '—'}</Typography>
              <Typography variant="subtitle2">Objectif long terme</Typography>
              <Typography sx={{ mb: 2 }}>{vision.longTermGoal || '—'}</Typography>
              <Typography variant="subtitle2">Inspirations</Typography>
              <Typography sx={{ mb: 2 }}>{vision.inspirations || '—'}</Typography>
              <Typography variant="subtitle2">Identité artistique</Typography>
              <Typography>{vision.artisticIdentity || '—'}</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(vision.motivations || []).map((m) => <Chip key={m} label={m} size="small" />)}
                {(vision.experiences || []).map((e) => (
                  <Chip key={e} label={e} size="small" color="secondary" variant="outlined" />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Matrice de compétences */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Matrice de compétences</Typography>
              <Grid container spacing={2}>
                {Object.entries(SKILL_LABELS).map(([key, label]) => {
                  const item    = skills[key] || {};
                  const current = Number(item.current) || 0;
                  const target  = Number(item.target) || 0;
                  return (
                    <Grid item xs={12} md={6} key={key}>
                      <Box sx={{ border: '1px solid #eee', borderRadius: 2, p: 2 }}>
                        <Typography sx={{ fontWeight: 'bold' }}>{label}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography variant="body2" sx={{ width: 80 }}>Actuel {current}/10</Typography>
                          <LinearProgress variant="determinate" value={current * 10} sx={{ flexGrow: 1, height: 8 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography variant="body2" sx={{ width: 80 }}>Objectif {target}/10</Typography>
                          <LinearProgress variant="determinate" value={target * 10} sx={{ flexGrow: 1, height: 8 }} color="secondary" />
                        </Box>
                        {item.coachComment && (
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>{item.coachComment}</Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>

          {/* Objectifs de saison */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Objectifs de saison</Typography>
              {seasonGoals.length ? seasonGoals.map((goal, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 'bold' }}>{goal.title}</Typography>
                    <Chip label={statusLabel(goal.status)} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">{goal.description}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <LinearProgress variant="determinate" value={Number(goal.progress) || 0} sx={{ flexGrow: 1, height: 8 }} />
                    <Typography variant="body2">{Number(goal.progress) || 0}%</Typography>
                  </Box>
                  {goal.deadline && (
                    <Typography variant="caption" color="text.secondary">
                      Deadline :{' '}
                      {goal.deadline.toDate
                        ? moment(goal.deadline.toDate()).format('DD/MM/YYYY')
                        : goal.deadline}
                    </Typography>
                  )}
                  {index < seasonGoals.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              )) : (
                <Typography color="text.secondary">Aucun objectif défini.</Typography>
              )}
            </CardContent>
          </Card>

          {/* ── Évaluations / Bilans ─────────────────────────────────────── */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <EvaluationList profileId={id} />
            </CardContent>
          </Card>
        </Grid>

        {/* ── Colonne droite ──────────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>

          {/* Forces & axes */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6">Forces</Typography>
              <List dense>
                {(profile.strengths || []).length
                  ? profile.strengths.map((x, i) => <ListItem key={i}><ListItemText primary={x} /></ListItem>)
                  : <Typography color="text.secondary">—</Typography>}
              </List>
              <Typography variant="h6" sx={{ mt: 1 }}>Axes d'amélioration</Typography>
              <List dense>
                {(profile.improvements || []).length
                  ? profile.improvements.map((x, i) => <ListItem key={i}><ListItemText primary={x} /></ListItem>)
                  : <Typography color="text.secondary">—</Typography>}
              </List>
            </CardContent>
          </Card>

          {/* Plan d'action */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Plan d'action</Typography>
              {actionPlan.length ? actionPlan.map((action, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 'bold' }}>{action.objective || 'Objectif'}</Typography>
                  <Typography>{action.action || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Échéance : {action.deadline || '—'}
                  </Typography>
                </Box>
              )) : (
                <Typography color="text.secondary">Aucune action définie.</Typography>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6">Notes visibles côté élève</Typography>
              <Typography sx={{ mb: 2 }}>{profile.studentNotes || '—'}</Typography>
              <Typography variant="h6">Notes privées coach/admin</Typography>
              <Typography>{profile.privateCoachNotes || '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentEvolutionDetails;